import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { GET_AVAILABLE_PLANS, GET_COMPANY_PLANS } from "graphql/platform";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";

type PlanOption = { id: string; appFeatureKeys: string[] };

type AvailablePlansData = {
  availablePlans: { appPlans: PlanOption[]; addOnPlans: PlanOption[] };
};

type CompanyPlansData = {
  companyPlans: { appPlanIds: string[]; addOnPlanIds: string[] };
};

export const useCompanyAppFeatureKeys = (
  companyId?: string | null,
  options?: { skip?: boolean },
): ReadonlySet<string> | null => {
  // Both queries are @auth(minRole: PlatformOwner).
  const canReadPlans = useCheckUserRole(Role.PlatformOwner);
  const skip = Boolean(options?.skip) || !companyId || !canReadPlans;

  const { data: companyPlansData } = useQuery<CompanyPlansData>(
    GET_COMPANY_PLANS,
    {
      variables: { companyId },
      skip,
      fetchPolicy: "cache-and-network",
    },
  );
  const { data: availablePlansData } = useQuery<AvailablePlansData>(
    GET_AVAILABLE_PLANS,
    { skip, fetchPolicy: "cache-first" },
  );

  return useMemo(() => {
    const assigned = companyPlansData?.companyPlans?.appPlanIds;
    const available = availablePlansData?.availablePlans;
    if (!assigned || !available) return null;

    const keysByPlanId = new Map(
      [...available.appPlans, ...available.addOnPlans].map((p) => [
        p.id,
        p.appFeatureKeys,
      ]),
    );
    const keys = new Set<string>();
    assigned.forEach((id) =>
      keysByPlanId.get(id)?.forEach((key) => keys.add(key)),
    );
    return keys;
  }, [companyPlansData, availablePlansData]);
};

export default useCompanyAppFeatureKeys;
